import React, { useState, useRef, useEffect, useMemo, forwardRef } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import useControllableState from '../hooks/useControllableState'
import useMergedRef from '../hooks/useMergeRef'
import capitalize from '../utils/capitalize'
import RangeCalendar from './RangeCalendar'
import BasePicker from './BasePicker'
import { useConfig } from '../ConfigProvider'

dayjs.extend(customParseFormat)

const validationRule = (val) =>
    Array.isArray(val) &&
    val.length === 2 &&
    val.every((v) => v instanceof Date)

const isFirstDateSet = (val) =>
    Array.isArray(val) && val.length === 2 && val[0] instanceof Date

const DAY_RANGE = { min: 1, max: 31 }
const MONTH_RANGE = { min: 1, max: 12 }
const YEAR_RANGE = { min: 1900, max: 2072 }

const rangeForTokenType = (type) => {
    if (type === 'day') return DAY_RANGE
    if (type === 'month') return MONTH_RANGE
    return YEAR_RANGE
}

const parseMaskGroups = (mask) => {
    const tokens = (mask || '').match(/[A-Za-z]+/g) || ['DD', 'MM', 'YYYY']
    return tokens.map((token) => {
        const first = token[0].toUpperCase()
        if (first === 'D') return { type: 'day', length: 2 }
        if (first === 'M') return { type: 'month', length: 2 }
        return { type: 'year', length: 4 }
    })
}

const parseMaskSeparator = (mask) => {
    const match = (mask || '').match(/[^A-Za-z0-9]/)
    return match ? match[0] : '/'
}

const isValidPrefix = (prefix, length, range) => {
    if (!prefix) return true
    const scale = Math.pow(10, length - prefix.length)
    const prefixNum = parseInt(prefix, 10)
    const rangeStart = prefixNum * scale
    const rangeEnd = rangeStart + scale - 1
    return rangeEnd >= range.min && rangeStart <= range.max
}

const buildFormattedDate = (rawDigits, maskGroups, maskSeparator) => {
    let result = ''
    let groupIndex = 0
    let groupDigits = ''
    for (let i = 0; i < rawDigits.length; i++) {
        const group = maskGroups[groupIndex]
        if (!group) break
        const candidate = groupDigits + rawDigits[i]
        if (!isValidPrefix(candidate, group.length, rangeForTokenType(group.type))) {
            break
        }
        if (groupDigits.length === 0 && groupIndex > 0) {
            result += maskSeparator
        }
        groupDigits = candidate
        result += rawDigits[i]
        if (groupDigits.length === group.length) {
            groupIndex += 1
            groupDigits = ''
        }
    }
    return result
}

const NumericDatePickerRange = forwardRef((props, ref) => {
    const {
        className,
        clearable,
        clearButton,
        closePickerOnChange,
        dateViewCount,
        dayClassName,
        dayStyle,
        defaultMonth,
        defaultOpen,
        defaultValue,
        defaultView,
        disabled,
        disableDate,
        enableHeaderLabel,
        disableOutOfMonth,
        firstDayOfWeek,
        hideOutOfMonthDates,
        hideWeekdays,
        inputFormat,
        inputPrefix,
        inputSuffix,
        labelFormat,
        seperator,
        locale,
        maxDate,
        minDate,
        onChange,
        onDropdownClose,
        onDropdownOpen,
        openPickerOnClear,
        renderDay,
        singleDate,
        size,
        style,
        value,
        weekendDays,
        yearLabelFormat,
        popperPlacement,
        ...rest
    } = props

    const { locale: themeLocale } = useConfig()

    const finalLocale = locale || themeLocale

    const dateFormat = inputFormat || 'DD/MM/YYYY'

    const maskGroups = useMemo(() => parseMaskGroups(dateFormat), [dateFormat])
    const maskSeparator = useMemo(() => parseMaskSeparator(dateFormat), [dateFormat])
    const singleDateDigitLength = useMemo(
        () => maskGroups.reduce((sum, group) => sum + group.length, 0),
        [maskGroups]
    )

    const [dropdownOpened, setDropdownOpened] = useState(defaultOpen)

    const inputRef = useRef()

    const [_value, setValue] = useControllableState({
        prop: value,
        defaultProp: defaultValue !== undefined ? defaultValue : [null, null],
        onChange,
    })

    const valueValid = validationRule(_value)
    const firstValueValid = isFirstDateSet(_value)

    const [inputText, setInputText] = useState('')
    const lastValue = useRef('')

    const [focused, setFocused] = useState(false)

    const suppressAutoOpenRef = useRef(false)

    const formatRangeText = (range) => {
        const firstDateLabel = range[0]
            ? capitalize(dayjs(range[0]).locale(finalLocale).format(dateFormat))
            : ''
        const secondDateLabel = range[1]
            ? capitalize(dayjs(range[1]).locale(finalLocale).format(dateFormat))
            : ''
        return `${firstDateLabel}${seperator}${secondDateLabel}`
    }

    const orderDates = (first, second) => {
        if (first instanceof Date && second instanceof Date && first.getTime() > second.getTime()) {
            return [second, first]
        }
        return [first, second]
    }

    useEffect(() => {
        if (valueValid && !focused) {
            const formatted = formatRangeText(_value)
            if (formatted !== lastValue.current) {
                setInputText(formatted)
                lastValue.current = formatted
            }
        } else if (!_value[0] && !_value[1] && !focused) {
            setInputText('')
            lastValue.current = ''
        }
    }, [_value, dateFormat, finalLocale, seperator, valueValid, focused])

    const formatInput = (rawVal, sep) => {
        const clean = rawVal.replace(/\D/g, '').slice(0, singleDateDigitLength * 2)
        const firstRaw = clean.slice(0, singleDateDigitLength)
        const secondRaw = clean.slice(singleDateDigitLength)

        let result = buildFormattedDate(firstRaw, maskGroups, maskSeparator)

        if (secondRaw.length > 0) {
            result += sep + buildFormattedDate(secondRaw, maskGroups, maskSeparator)
        }

        return result
    }

    const isDateWithinRange = (dateStr) => {
        if (!dateStr) return false
        const parts = dateStr.split(/[^0-9]/)
        if (parts.length !== maskGroups.length) return false

        const values = {}
        parts.forEach((part, idx) => {
            values[maskGroups[idx].type] = parseInt(part, 10)
        })

        const { day, month, year } = values
        if (isNaN(day) || day < DAY_RANGE.min || day > DAY_RANGE.max) return false
        if (isNaN(month) || month < MONTH_RANGE.min || month > MONTH_RANGE.max) return false
        if (isNaN(year) || year < YEAR_RANGE.min || year > YEAR_RANGE.max) return false
        return true
    }

    const isValidRange = (startStr, endStr) => {
        if (!isDateWithinRange(startStr) || !isDateWithinRange(endStr)) return false

        const startDate = dayjs(startStr, dateFormat, finalLocale)
        const endDate = dayjs(endStr, dateFormat, finalLocale)

        return startDate.isValid() && endDate.isValid()
    }

    const parseAndSetDates = (formattedStr) => {
        const parts = formattedStr.split(seperator)
        if (parts.length === 2) {
            const startStr = parts[0]
            const endStr = parts[1]

            if (startStr.length === 10 && endStr.length === 10) {
                if (isValidRange(startStr, endStr)) {
                    const startDate = dayjs(startStr, dateFormat, true)
                    const endDate = dayjs(endStr, dateFormat, true)

                    if (startDate.isValid() && endDate.isValid()) {
                        const range = orderDates(startDate.toDate(), endDate.toDate())
                        setValue(range, true)
                        const formatted = formatRangeText(range)
                        setInputText(formatted)
                        lastValue.current = formatted
                        if (closePickerOnChange) {
                            setDropdownOpened(false)
                            onDropdownClose?.()
                        }
                    }
                }
            }
        } else if (parts.length === 1) {
            const startStr = parts[0]
            if (startStr.length === 10) {
                if (isDateWithinRange(startStr)) {
                    const startDate = dayjs(startStr, dateFormat, true)
                    if (startDate.isValid()) {
                        setValue([startDate.toDate(), null], true)
                    }
                }
            }
        }
    }

    const setDatesFromInput = () => {
        const parts = inputText.split(seperator)
        if (parts.length === 2) {
            const startStr = parts[0]
            const endStr = parts[1]
            if (startStr.length === 10 && endStr.length === 10) {
                if (isValidRange(startStr, endStr)) {
                    const startDate = dayjs(startStr, dateFormat, true).toDate()
                    const endDate = dayjs(endStr, dateFormat, true).toDate()
                    const range = orderDates(startDate, endDate)
                    setValue(range, true)
                    const formatted = formatRangeText(range)
                    setInputText(formatted)
                    lastValue.current = formatted
                    return
                }
            }
        } else if (parts.length === 1) {
            const startStr = parts[0]
            if (startStr.length === 10) {
                if (isDateWithinRange(startStr)) {
                    const startDate = dayjs(startStr, dateFormat, true).toDate()
                    setValue([startDate, null], true)
                    lastValue.current = inputText
                    return
                }
            }
        }

        if (valueValid) {
            const formatted = formatRangeText(_value)
            setInputText(formatted)
            lastValue.current = formatted
        } else {
            setValue([null, null])
            setInputText('')
            lastValue.current = ''
        }
    }

    const handleInputBlur = (event) => {
        let isValid = true
        if (inputText) {
            const parts = inputText.split(seperator)
            if (parts.length === 2) {
                const startStr = parts[0]
                const endStr = parts[1]
                if (startStr.length !== 10 || endStr.length !== 10 || !isValidRange(startStr, endStr)) {
                    isValid = false
                }
            } else if (parts.length === 1) {
                const startStr = parts[0]
                if (startStr.length !== 10 || !isDateWithinRange(startStr)) {
                    isValid = false
                }
            } else {
                isValid = false
            }
        }
        typeof rest.onBlur === 'function' && rest.onBlur(event, isValid)
        setFocused(false)
        setDatesFromInput()
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            setDropdownOpened(false)
            setDatesFromInput()
        }
    }

    const handleInputFocus = (event) => {
        typeof rest.onFocus === 'function' && rest.onFocus(event)
        setFocused(true)
        if (suppressAutoOpenRef.current) {
            suppressAutoOpenRef.current = false
            return false
        }
    }

    const handleInputChange = (e) => {
        let val = e.target.value

        if (val.length < lastValue.current.length) {
            const lastCharDeleted = lastValue.current.slice(val.length)
            if (lastCharDeleted.match(/[^0-9]/)) {
                const temp = val.replace(/[^0-9]+$/, '')
                val = temp.slice(0, -1)
            }
        }

        const formatted = formatInput(val, seperator)
        setInputText(formatted)
        lastValue.current = formatted

        parseAndSetDates(formatted)
    }

    const handleValueChange = (range) => {
        setValue(range, true)
        if (closePickerOnChange && validationRule(range)) {
            setDropdownOpened(false)
            onDropdownClose?.()
            suppressAutoOpenRef.current = true
            window.setTimeout(() => inputRef.current?.focus(), 0)
        }
    }

    const handleClear = () => {
        setValue([null, null], true)
        setInputText('')
        lastValue.current = ''
        setDropdownOpened(true)
        openPickerOnClear && onDropdownOpen?.()
        inputRef.current?.focus()
    }

    

    return (
        <BasePicker
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            ref={useMergedRef(ref, inputRef)}
            size={size}
            style={style}
            className={className}
            inputLabel={inputText}
            clearable={clearable && firstValueValid}
            clearButton={clearButton}
            onClear={handleClear}
            dateViewCount={dateViewCount}
            onDropdownClose={onDropdownClose}
            onDropdownOpen={onDropdownOpen}
            disabled={disabled}
            inputPrefix={inputPrefix}
            inputSuffix={inputSuffix}
            inputtable={true}
            inputtableBlurClose={false}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            {...rest}
        >
            <RangeCalendar
                locale={finalLocale}
                defaultMonth={valueValid ? _value[0] : defaultMonth}
                value={_value}
                onChange={handleValueChange}
                labelFormat={labelFormat}
                dayClassName={dayClassName}
                dayStyle={dayStyle}
                disableOutOfMonth={disableOutOfMonth}
                minDate={minDate}
                maxDate={maxDate}
                disableDate={disableDate}
                firstDayOfWeek={firstDayOfWeek}
                enableHeaderLabel={enableHeaderLabel}
                singleDate={singleDate}
                dateViewCount={dateViewCount}
                defaultView={defaultView}
                hideOutOfMonthDates={hideOutOfMonthDates}
                hideWeekdays={hideWeekdays}
                renderDay={renderDay}
                weekendDays={weekendDays}
                yearLabelFormat={yearLabelFormat}
            />
        </BasePicker>
    )
})

NumericDatePickerRange.defaultProps = {
    closePickerOnChange: true,
    labelFormat: {
        month: 'MMM',
        year: 'YYYY',
    },
    defaultOpen: false,
    seperator: ' -> ',
    clearable: true,
    firstDayOfWeek: 'monday',
    singleDate: false,
    dateViewCount: 1,
    openPickerOnClear: false,
}

export default NumericDatePickerRange
